import { getWebsiteConsent, trackGa4Event, trackWebsiteEvent } from "./analytics";

export type HeroVariantId = "mission" | "catalogue";

export type HeroVariant = {
  id: HeroVariantId;
  eyebrow: string;
  titleLineOne: string;
  titleLineTwo: string;
  titleEmphasis: string;
  lede: string;
  primaryLabel: string;
  secondaryLabel: string;
  proof: string;
  captionLineOne: string;
  captionLineTwo: string;
};

export const HERO_EXPERIMENT_NAME = "homepage_hero_v2";
const ASSIGNMENT_KEY = "greenswap-web-homepage-hero-v2";

export const heroVariants: Record<HeroVariantId, HeroVariant> = {
  mission: {
    id: "mission",
    eyebrow: "Life beyond the shelf",
    titleLineOne: "Set useful",
    titleLineTwo: "things",
    titleEmphasis: "free.",
    lede: "Find a useful next life for what you no longer use: trade, give, lend, donate, or repair it locally.",
    primaryLabel: "See how GreenSwap works",
    secondaryLabel: "See the bigger story",
    proof: "Local first · Community powered · Impact reported honestly",
    captionLineOne: "A lamp leaves one home.",
    captionLineTwo: "Its usefulness does not.",
  },
  catalogue: {
    id: "catalogue",
    eyebrow: "Your things can do more",
    titleLineOne: "Know what you have.",
    titleLineTwo: "Free what you",
    titleEmphasis: "don’t need.",
    lede: "Catalogue useful things, remember where they are, and give unused items a new life through people and communities you trust.",
    primaryLabel: "See how GreenSwap works",
    secondaryLabel: "See the bigger story",
    proof: "Know it · Find it · Circulate it when you are ready",
    captionLineOne: "More clarity for your space.",
    captionLineTwo: "More life for useful things.",
  },
};

export function resolveHeroVariant(): { variant: HeroVariant; preview: boolean } {
  const requested = new URLSearchParams(window.location.search).get("hero");
  if (requested === "mission" || requested === "catalogue") {
    return { variant: heroVariants[requested], preview: true };
  }
  if (!getWebsiteConsent()?.analytics) return { variant: heroVariants.mission, preview: false };
  const stored = window.localStorage.getItem(ASSIGNMENT_KEY);
  if (stored === "mission" || stored === "catalogue") {
    return { variant: heroVariants[stored], preview: false };
  }
  const assigned: HeroVariantId = window.crypto.getRandomValues(new Uint32Array(1))[0] % 2 === 0 ? "mission" : "catalogue";
  window.localStorage.setItem(ASSIGNMENT_KEY, assigned);
  return { variant: heroVariants[assigned], preview: false };
}

export function trackHeroExposure(variant: HeroVariantId, preview: boolean): void {
  if (preview) return;
  trackWebsiteEvent("flag.exposure", { flag: HERO_EXPERIMENT_NAME, variant });
  trackGa4Event("experiment_exposure", { experiment: HERO_EXPERIMENT_NAME, variant });
}

export function trackHeroAction(variant: HeroVariantId, action: "primary" | "secondary", preview: boolean): void {
  if (preview) return;
  trackWebsiteEvent("user_action", {
    action: `hero_${action}`,
    page: "/",
    surface: `hero_${variant}`,
  });
  trackGa4Event("hero_action", { experiment: HERO_EXPERIMENT_NAME, variant, action });
}
